import { useMemo } from 'react';
import { useSearchCareProgramsQuery } from 'services/careProgramService';
import { useLoggedInUser } from 'features/auth/hooks/useAuthData';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { transformCareProgram, groupProgramsBySections } from '../utils/careProgramUtils';
import type { ProgramSection } from '../types';

export interface UseCareProgramSearchResult {
  sections: ProgramSection[];
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
  resultCount: number;
}

export const useCareProgramSearch = (searchTerm: string): UseCareProgramSearchResult => {
  const user = useLoggedInUser();
  const { translation, currentLocale } = useTranslationContext();
  const memberUuid = user?.uuid;
  const query = searchTerm.trim();
  const { data, isLoading, isFetching, error, refetch } = useSearchCareProgramsQuery(
    { memberUuid: memberUuid ?? '', query, locale: currentLocale },
    { skip: !query || !memberUuid },
  );

  const sections = useMemo(() => {
    const programs = (data ?? [])
      .filter((program) => program.care_program_mappings?.is_member_visible)
      .map(transformCareProgram)
      .filter((program): program is NonNullable<typeof program> => program !== null);

    return groupProgramsBySections(programs, undefined, translation);
  }, [data, translation]);

  const resultCount = sections.reduce((count, section) => count + section.programs.length, 0);

  return { sections, isLoading: isLoading || isFetching, error, refetch, resultCount };
};
